import { EVENT_LOG_SEARCH_MIN, type EventLogFilter } from '@nest-recruit/shared';
import { addDhakaDays, dhakaDayStartIso, dhakaToday } from '../../../../lib/dhaka-time';
import { formatIsoDate } from '../../../../lib/format';

// Admin · Event log's address (06-qol §3 and §6): every filter, the page, the open event and one record's story, so a
// copied link shows a colleague the same view. The names are the API's own (`?eventType=…&ventureId=…`), plus `when`
// for the quick windows, `event` for the open row and `story`/`storyType` for the drill-down. Nothing in the
// router validates these (the route is not this page's to change), so the page reads them here and drops anything
// malformed rather than failing.

/** "When": Today is the default and is never written into the address. */
export const EVENT_LOG_WINDOWS = ['today', '7', '30', 'all'] as const;
export type EventLogWindow = (typeof EVENT_LOG_WINDOWS)[number];

export interface EventLogSearch {
  readonly q?: string;
  readonly entityType?: string;
  readonly eventType?: string;
  readonly actorType?: 'user' | 'system';
  /** "Everything by this person", from a link; there is no picker for it. */
  readonly actorId?: string;
  readonly ventureId?: string;
  /** A whole record id only: a part of one is never a filter (it was counted, and never sent). */
  readonly entityId?: string;
  /** A quick window other than Today. */
  readonly when?: Exclude<EventLogWindow, 'today'>;
  /** A chosen range, as Dhaka calendar days (`YYYY-MM-DD`). Either end may be open. Wins over `when`. */
  readonly from?: string;
  readonly to?: string;
  readonly page?: number;
  /** The row open in the pane. */
  readonly event?: string;
  /** One record's story: its id and kind. */
  readonly story?: string;
  readonly storyType?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const CODE = /^[a-z0-9_.]{1,80}$/;

/** True for a whole record id. */
export function isWholeId(value: string): boolean {
  return UUID.test(value.trim());
}

/** Text from the address: TanStack's reader turns `?q=2024` or `?when=7` into a number, so a number is read back as text. */
function text(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function id(value: unknown): string | undefined {
  const read = text(value);
  return read && UUID.test(read) ? read.toLowerCase() : undefined;
}

function code(value: unknown): string | undefined {
  const read = text(value);
  return read && CODE.test(read) ? read : undefined;
}

function day(value: unknown): string | undefined {
  const read = text(value);
  return read && DAY.test(read) && dhakaDayStartIso(read) ? read : undefined;
}

/** The page's reading of the address. Anything unknown or malformed is dropped, never an error. */
export function readEventLogSearch(raw: Readonly<Record<string, unknown>>): EventLogSearch {
  const next: { -readonly [K in keyof EventLogSearch]: EventLogSearch[K] } = {};
  const q = text(raw.q);
  if (q !== undefined) next.q = q.slice(0, 120);
  const entityType = code(raw.entityType);
  if (entityType) next.entityType = entityType;
  const eventType = code(raw.eventType);
  if (eventType) next.eventType = eventType;
  const actorType = text(raw.actorType);
  if (actorType === 'user' || actorType === 'system') next.actorType = actorType;
  const actorId = id(raw.actorId);
  if (actorId) next.actorId = actorId;
  const ventureId = id(raw.ventureId);
  if (ventureId) next.ventureId = ventureId;
  const entityId = id(raw.entityId);
  if (entityId) next.entityId = entityId;
  const from = day(raw.from);
  if (from) next.from = from;
  const to = day(raw.to);
  if (to) next.to = to;
  const when = text(raw.when);
  if (next.from === undefined && next.to === undefined && (when === '7' || when === '30' || when === 'all')) next.when = when;
  const page = Number(raw.page);
  if (Number.isInteger(page) && page > 1) next.page = page;
  const event = id(raw.event);
  if (event) next.event = event;
  const story = id(raw.story);
  const storyType = code(raw.storyType);
  if (story && storyType) {
    next.story = story;
    next.storyType = storyType;
  }
  return next;
}

/** The address as text, in a fixed order, for `history.replace`. */
export function eventLogHref(search: EventLogSearch): string {
  const params = new URLSearchParams();
  for (const key of ['q', 'entityType', 'eventType', 'actorType', 'actorId', 'ventureId', 'entityId', 'when', 'from', 'to', 'page', 'story', 'storyType', 'event'] as const) {
    const value = search[key];
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const query = params.toString();
  return `/admin/events${query === '' ? '' : `?${query}`}`;
}

type FilterPatch = Partial<Omit<EventLogSearch, 'page' | 'event' | 'story' | 'storyType'>>;

/**
 * A filter change: back to the first page, the open row closed (it may not be in the new set). A range replaces the
 * quick window and the reverse. Picking one record (its id) widens Today to Any time: a record's past is the point.
 */
export function withFilters(search: EventLogSearch, patch: FilterPatch): EventLogSearch {
  const next: Record<string, unknown> = { ...search, ...patch };
  delete next.page;
  delete next.event;
  if ('when' in patch) {
    delete next.from;
    delete next.to;
  }
  if ('from' in patch || 'to' in patch) delete next.when;
  const isNewRecord = patch.entityId !== undefined && patch.entityId !== search.entityId;
  if (isNewRecord && !('when' in patch) && search.when === undefined && search.from === undefined && search.to === undefined) next.when = 'all';
  for (const key of Object.keys(next)) if (next[key] === undefined || next[key] === '') delete next[key];
  return next as EventLogSearch;
}

/** The list's own part of the address: no story, no open row, no page. */
export function listSearchOf(search: EventLogSearch): EventLogSearch {
  const { story: _story, storyType: _storyType, event: _event, page: _page, ...rest } = search;
  return rest;
}

/** The last instant of a Dhaka day: one millisecond before the next day starts, so nothing in its last second is lost. */
function dayEndIso(date: string): string | null {
  const next = dhakaDayStartIso(addDhakaDays(date, 1));
  return next ? new Date(Date.parse(next) - 1).toISOString() : null;
}

/** What the table, the count, "N new" and the export ask the server for, "When" resolved as Dhaka days at `now`. */
export function filtersOf(search: EventLogSearch, now: Date = new Date()): EventLogFilter {
  const today = dhakaToday(now);
  let from: string | null = null;
  let to: string | null = null;
  if (search.from || search.to) {
    from = search.from ? dhakaDayStartIso(search.from) : null;
    to = search.to ? dayEndIso(search.to) : null;
  } else if (search.when === '7' || search.when === '30') {
    from = dhakaDayStartIso(addDhakaDays(today, -(Number(search.when) - 1)));
  } else if (search.when !== 'all') {
    from = dhakaDayStartIso(today);
  }
  const q = search.q?.trim();
  return {
    ...(search.entityType ? { entityType: search.entityType } : {}),
    ...(search.entityId ? { entityId: search.entityId } : {}),
    ...(search.eventType ? { eventType: search.eventType } : {}),
    ...(search.actorId ? { actorId: search.actorId } : {}),
    ...(search.actorType ? { actorType: search.actorType } : {}),
    ...(search.ventureId ? { ventureId: search.ventureId } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(q && q.length >= EVENT_LOG_SEARCH_MIN ? { q } : {}),
  };
}

/** How many filters narrow the table, for "Clear N filters". Today, the default, is not one. */
export function filterCount(search: EventLogSearch): number {
  const isSearch = (search.q?.trim().length ?? 0) >= EVENT_LOG_SEARCH_MIN;
  return [search.entityType, search.eventType, search.actorType ?? search.actorId, search.ventureId, search.entityId, search.when ?? search.from ?? search.to, isSearch ? search.q : undefined].filter(
    (value) => value !== undefined,
  ).length;
}

/** "Today", "Last 7 days", "Any time", "12-10-2026 to 20-10-2026", "From 12-10-2026", "Until 20-10-2026". */
export function whenLabel(search: EventLogSearch): string {
  if (search.from && search.to) return search.from === search.to ? formatIsoDate(search.from) : `${formatIsoDate(search.from)} to ${formatIsoDate(search.to)}`;
  if (search.from) return `From ${formatIsoDate(search.from)}`;
  if (search.to) return `Until ${formatIsoDate(search.to)}`;
  if (search.when === 'all') return 'Any time';
  if (search.when) return `Last ${search.when} days`;
  return 'Today';
}

/** The count's tail, matching the When pill: "1,284 events today", "… in the last 7 days", "… from 12-10-2026". */
export function whenTail(search: EventLogSearch): string {
  const label = whenLabel(search);
  if (label === 'Today') return ' today';
  if (search.when === 'all') return '';
  const lower = `${label.charAt(0).toLowerCase()}${label.slice(1)}`;
  if (search.when) return ` in the ${lower}`;
  return search.from && search.to ? (search.from === search.to ? ` on ${label}` : ` from ${lower}`) : ` ${lower}`;
}
